import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../components/Header";
import ShoppingBreadcrumb from "../components/ShoppingBreadcrumb";
import ProductDetailSection from "../components/product-detail/ProductDetailSection";
import ProductDetailState from "../components/product-detail/ProductDetailState";
import NewArrivalOne from "../components/NewArrivalOne";
import Newsletter from "../components/Newsletter";
import Footer from "../components/Footer";
import BottomFooter from "../components/BottomFooter";
import ColorInit from "../helper/ColorInit";
import CartSegmentWarningModal from "../components/CartSegmentWarningModal";
import GuestLoginRequiredModal from "../components/GuestLoginRequiredModal";
import { addCartItem, getProductDetails, logOut, removeCartItem, updateCartItemPortion, updateCartItemQuantity } from "../redux/actions/action-creator";
import { getProductPrice, getProductUnit, getProductVariantId } from "../utils/productOptions";
import { getProductDetailsData } from "../utils/productDetails";
import { getActiveCartStoreName, hasCartSegmentMismatch } from "../utils/cartSegmentGuard";
import { isGuestUser, isSuccessResponse } from "../utils/guestLogin";
import { getMarketMetadata } from "../utils/locationDefaults";
import { setPageSeo } from "../utils/seo";
import "./HomePage.css";

const EMPTY = {};

export default function ProductDetail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const auth = useSelector((state) => state.auth || EMPTY);
  const cart = useSelector((state) => state.cart || EMPTY);
  const storesData = useSelector((state) => state.common?.storesData);
  const context = location.state || EMPTY;
  const query = new URLSearchParams(location.search);
  const productId = String(params.productId || query.get("product_variant_id") || getProductVariantId(context.product || EMPTY) || "");
  const segmentId = String(query.get("segment_id") || context.segmentId || context.product?.segment_id || context.cartContext?.segment_id || "");
  const businessId = String(query.get("business_segment_id") || context.businessSegmentId || context.product?.business_segment_id || context.cartContext?.business_segment_id || "");
  const canLoad = Boolean(productId && !productId.startsWith("preview-") && segmentId && businessId);
  const requestKey = JSON.stringify({ product_variant_id: productId, segment_id: segmentId, business_segment_id: businessId });
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState(null);
  const requestRef = useRef(null);
  const addingRef = useRef(false);
  const [pendingCartItem, setPendingCartItem] = useState(null);
  const [guestModal, setGuestModal] = useState(false);

  useEffect(() => {
    if (!canLoad) return undefined;
    let active = true;
    setResult({ key: requestKey, loading: true });
    const key = `${requestKey}:${attempt}`;
    // Reuse the request when StrictMode replays the mount effect.
    if (requestRef.current?.key !== key) {
      const payload = new FormData();
      Object.entries(JSON.parse(requestKey)).forEach(([name, value]) => payload.append(name, value));
      requestRef.current = { key, promise: dispatch(getProductDetails(payload)) };
    }
    requestRef.current.promise.then((response) => {
      if (active) setResult({ key: requestKey, loading: false, ...getProductDetailsData(response) });
    }).catch((error) => {
      if (active) setResult({ key: requestKey, loading: false, error: error?.message || "We couldn't load this product. Please try again." });
    });
    return () => { active = false; };
  }, [attempt, canLoad, dispatch, requestKey]);

  useEffect(() => { setPendingCartItem(null); }, [requestKey]);

  const current = canLoad && result?.key === requestKey ? result : null;
  const loading = canLoad && (!current || current.loading);
  const market = getMarketMetadata({ ...(auth.user_location || EMPTY), country_id: auth.country_id });
  const product = current?.product ? {
    ...current.product,
    product_variant_id: current.product.product_variant_id || productId,
    segment_id: current.product.segment_id || segmentId,
    business_segment_id: current.product.business_segment_id || businessId,
    currency: current.product.currency || context.product?.currency || market.currency,
  } : null;
  const title = product?.title || "Product details";
  const cartBusy = Boolean(cart.saveCartLoading || cart.getCartLoading);
  const cartItem = product && !hasCartSegmentMismatch(cart, product)
    ? (cart.cartItems || []).find((item) => String(getProductVariantId(item)) === String(getProductVariantId(product)))
    : null;
  const detailContext = { ...context, segmentId, businessSegmentId: businessId };

  useEffect(() => {
    setPageSeo({ title: `${title} | RGHT NOW`, description: `Explore ${title} and more products on RGHT NOW.`, path: location.pathname });
  }, [location.pathname, title]);

  const saveToCart = async (item, replaceCart = false) => {
    if (!item || addingRef.current || cartBusy) return;
    addingRef.current = true;
    try {
      const response = await dispatch(addCartItem(item, { replaceCart }));
      if (isSuccessResponse(response)) toast.success("Added to your basket.");
      else toast.error(response?.message || "Unable to add this product. Please try again.");
    } catch (error) {
      toast.error(error?.message || "Unable to add this product. Please try again.");
    } finally {
      addingRef.current = false;
      setPendingCartItem(null);
    }
  };

  const addProduct = ({ portion, price }) => {
    if (!product || cartBusy || addingRef.current) return;
    if (isGuestUser(auth.userData)) { setGuestModal(true); return; }
    const item = {
      ...product, id: String(getProductVariantId(product)), name: title,
      price, portion, quantity: 1, unit: portion ? "" : getProductUnit(product),
    };
    if (hasCartSegmentMismatch(cart, item)) setPendingCartItem(item);
    else saveToCart(item);
  };

  const updateCart = async (action) => {
    if (addingRef.current || cartBusy) return false;
    addingRef.current = true;
    try {
      const response = await dispatch(action);
      if (isSuccessResponse(response)) return true;
      toast.error(response?.message || "Unable to update your basket. Please try again.");
    } catch (error) {
      toast.error(error?.message || "Unable to update your basket. Please try again.");
    } finally {
      addingRef.current = false;
    }
    return false;
  };

  const changeQuantity = (quantity) => {
    if (!cartItem || !Number.isInteger(quantity) || quantity < 0) return;
    if (isGuestUser(auth.userData)) { setGuestModal(true); return; }
    const id = String(getProductVariantId(cartItem));
    return updateCart(quantity === 0 ? removeCartItem(id) : updateCartItemQuantity(id, quantity));
  };

  const changePortion = (portion) => {
    if (!cartItem) return true;
    if (isGuestUser(auth.userData)) { setGuestModal(true); return false; }
    return updateCart(updateCartItemPortion(String(getProductVariantId(cartItem)), {
      portion: portion.portion,
      price: Number(getProductPrice(product, portion)),
    }));
  };

  return (
    <div className="productdetail-page home-page-shell">
      <ColorInit color={false} />
      <Header category={true} />
      <ShoppingBreadcrumb page="product" product={product} businessSegmentId={businessId} segmentId={segmentId} />
      <main>
        {product ? <>
          <ProductDetailSection key={requestKey} product={product} cartItem={cartItem} adding={cartBusy} onAddToCart={addProduct} onQuantityChange={changeQuantity} onPortionChange={changePortion} />
          <NewArrivalOne recentlybrowse={current.recommendations} productDetailContext={detailContext} cartContext={{ segment_id: segmentId, business_segment_id: businessId }} />
        </> : <ProductDetailState loading={loading} error={current?.error} missingContext={!canLoad} onRetry={canLoad ? () => setAttempt((value) => value + 1) : undefined} />}
      </main>
      <Newsletter />
      <Footer />
      <BottomFooter />
      <CartSegmentWarningModal show={Boolean(pendingCartItem)} storeName={getActiveCartStoreName(cart, storesData)} submitting={cartBusy}
        onClose={() => { if (!cartBusy) setPendingCartItem(null); }} onConfirm={() => saveToCart(pendingCartItem, true)} />
      <GuestLoginRequiredModal show={guestModal} onClose={() => setGuestModal(false)} onContinue={async () => { setGuestModal(false); await dispatch(logOut()); navigate("/login"); }} />
    </div>
  );
}
